import { config } from '@iheartjane/config';
import { trackError } from '@iheartjane/tracking';
import type { AwsBody, AwsS3UploadParameters } from '@uppy/aws-s3';
import AwsS3 from '@uppy/aws-s3';
import type { Body, Meta, UppyFile, UppyOptions } from '@uppy/core';
import { Uppy } from '@uppy/core';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import { useEffect, useMemo, useState } from 'react';

import { isInMaintenance, maintenanceReload } from '@jane/shared/util';

import { ALLOWED_FILE_TYPES, AllowedFiletypes } from './allowedFileTypes';
import { formatFileData } from './formatFileData';
import { processPdf } from './processPdf';
import type { ProcessedFile } from './processPdf';

enum UploadStatus {
  Failed = 'failed',
  InProgress = 'inProgress',
  NotStarted = 'notStarted',
  Success = 'success',
}

const UPLOAD_FAILED_MESSAGE = 'Unable to upload image. Please try again';
const THUMBNAIL_SETTINGS = {
  id: 'ThumbnailGenerator',
  thumbnailWidth: 200,
  thumbnailHeight: 200,
  waitForThumbnailsBeforeUpload: false,
};

const UPPY_INIT_SETTINGS: UppyOptions<Meta, AwsBody> = {
  autoProceed: true,
  locale: {
    pluralize: (n) => (n === 1 ? 0 : 1),
    strings: {
      exceedsSize2:
        'The file is too large to upload. Please select a smaller file and try again.',
    },
  },
  logger: {
    debug: () => null,
    warn: () => null,
    error: () => {
      trackError(new Error('Uppy file upload error'));
    },
  },
  restrictions: {
    allowedFileTypes: ALLOWED_FILE_TYPES,
    maxNumberOfFiles: 1,
    maxFileSize: 10 * 1024 * 1024,
    maxTotalFileSize: 10 * 1024 * 1024,
    minNumberOfFiles: 1,
    minFileSize: 0,
    requiredMetaFields: [],
  },
};

const getUploadParameters = async (
  file: UppyFile<Meta, Body>
): Promise<AwsS3UploadParameters> => {
  const url = `${config.apiPath}/s3/params?filename=${file.name}&type=${file.type}`;
  const response = await fetch(url, {
    method: 'get',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
    },
  });
  if (isInMaintenance(response)) {
    maintenanceReload();

    // Stubbed return value. In maintenance mode, the page will reload.
    return {
      method: 'POST',
      url: '',
      fields: {},
    };
  }
  const data = await response.json();
  return {
    method: data.method,
    url: data.url,
    fields: data.fields,
  };
};

export const useUppy = ({
  image,
  name,
  onChange,
}: {
  image?: string;
  name: string;
  onChange: (file: string) => void;
}) => {
  const [thumbnail, setThumbnail] = useState<null | string>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<null | string>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    image ? UploadStatus.Success : UploadStatus.NotStarted
  );

  const uppy = useMemo(() => {
    return new Uppy<Meta, AwsBody>({ ...UPPY_INIT_SETTINGS, id: name })
      .use(ThumbnailGenerator, THUMBNAIL_SETTINGS)
      .on('thumbnail:generated', (_file, preview) => {
        setThumbnail(preview);
      })
      .on('error', (error: Error) => {
        setError(error.message);
        setUploadStatus(UploadStatus.Failed);
      })
      .on('restriction-failed', (_file, error: Error) => {
        setError(error.message);
        setUploadStatus(UploadStatus.Failed);
      })
      .use(AwsS3, { getUploadParameters, shouldUseMultipart: false })
      .on('complete', function onUploadComplete(result) {
        if (result.failed?.length) {
          setError(UPLOAD_FAILED_MESSAGE);
          resetUploader();
          setUploadStatus(UploadStatus.Failed);
        } else {
          setError(null);
        }
      })
      .on('upload-success', function onUploadSuccess() {
        setProgress(0);
        setUploadStatus(UploadStatus.Success);
      })
      .on('upload-progress', (_, { bytesUploaded, bytesTotal }) => {
        setProgress(bytesTotal ? (bytesUploaded / bytesTotal) * 100 : 0);
      })
      .on('upload-error', function handleUppyError() {
        setError(UPLOAD_FAILED_MESSAGE);
        setUploadStatus(UploadStatus.Failed);
      });
  }, [name]);

  const resetUploader = () => {
    setUploadStatus(UploadStatus.NotStarted);
    setProgress(0);
    setThumbnail(null);
  };

  const handleRemoveFile = () => {
    uppy.cancelAll();
    resetUploader();
    setError(null);
    onChange('');
  };

  const handleChange = async (file?: File) => {
    setError(null);
    setUploadStatus(UploadStatus.InProgress);

    if (!file) return;

    if (file.type === AllowedFiletypes.PDF) {
      processPdf({ file, setError }, (processedFile: ProcessedFile) => {
        uppy.addFile(processedFile);
      });
      return;
    }

    try {
      const id = uppy.addFile({
        source: 'file input',
        name: file.name,
        type: file.type,
        data: file,
      });
      await uppy.upload();
      const uploadedFile = uppy.getFile(id);
      const fileData = formatFileData(uploadedFile);

      if (!fileData.id) {
        trackError(
          'Upload succeeded without providing a reference to the file.',
          {
            uploadedFile,
          }
        );
        throw new Error('error uploading file');
      }
      // Shrine wants this format when storing images: JSON.stringify(fileData)
      onChange(fileData.id);
    } catch {
      setError('error uploading file');
    }
  };

  useEffect(() => {
    return () => uppy.destroy();
  }, [uppy]);

  return {
    error,
    changeFile: handleChange,
    progress,
    removeFile: handleRemoveFile,
    status: uploadStatus,
    thumbnail,
  };
};
